import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart, CartItem } from '../context/CartContext';
import { usePoints } from '../context/PointsContext';
import { NairaBagText } from '../components/NairaBagText';
import { API_URL } from '../constants/config';
import './AIChat.css';

type Sender = 'user' | 'ai';

interface Message {
  id: string;
  text: string;
  sender: Sender;
  timestamp: Date;
}

interface Order {
  _id: string;
  orderReference?: string;
  status?: string;
  total?: number;
  createdAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
}

interface Product {
  _id: string;
  name: string;
  price: number;
  image?: string;
  isFeatured?: boolean;
  isPopular?: boolean;
}

const SUPPORT_EMAIL = 'hello@gimbiyastore.com';
const SUPPORT_PHONE = '07065012438';
const LOGISTICS_PHONE = '08162437615';
const HEAD_OFFICE_PHONE = '07010110977';

const LOCATION = 'Zango, Zaria, Kaduna State, Nigeria';

const MINIMUM_ORDER_AMOUNT = 15000;
const MINIMUM_PART_PAYMENT_PERCENT = 30;

const initialText = (name?: string) => `Hello${
  name ? ` ${name.split(' ')[0]}` : ''
}! 👋 I'm Amaya, your shopping assistant.

I can help with:
• Orders and tracking
• Delivery times
• Paystack payments
• Returns and refunds
• Products and shopping
• Reward points and accounts
• Password reset and account recovery

🚚 Delivery:
• Zaria/Kaduna: within 24 hours
• Outside Zaria/Kaduna: 1–3 days
• Shipping: free for all orders

Ask me a question to get started.`;

const AIChat: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { cart, totalItems, totalPrice } = useCart();
  const { points, pointsToCurrency } = usePoints();

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: initialText(user?.name),
      sender: 'ai',
      timestamp: new Date()
    }
  ]);

  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const productsCacheRef = useRef<Product[] | null>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return;

    const scroll = () =>
      requestAnimationFrame(() => {
        wrapper.scrollTop = wrapper.scrollHeight;
      });

    scroll();

    window.visualViewport?.addEventListener('resize', scroll);
    window.visualViewport?.addEventListener('scroll', scroll);

    return () => {
      window.visualViewport?.removeEventListener('resize', scroll);
      window.visualViewport?.removeEventListener('scroll', scroll);
    };
  }, [messages, isTyping]);

  const fetchOrders = async (): Promise<Order[]> => {
    if (!isAuthenticated) return [];

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/orders/my-orders`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) return [];

      const data = await response.json();

      const list = Array.isArray(data)
        ? data
        : data.orders || data.data || [];

      return list
        .filter((item: Order) => item?._id)
        .sort(
          (a: Order, b: Order) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
        );
    } catch {
      return [];
    }
  };

  const fetchProducts = async (): Promise<Product[]> => {
    if (productsCacheRef.current !== null) {
      return productsCacheRef.current;
    }

    try {
      const response = await fetch(
        `${API_URL}/products/ai-recommendations`
      );

      if (!response.ok) return [];

      const data = await response.json();
      const products = data.products || data.data || [];

      if (!Array.isArray(products)) return [];

      productsCacheRef.current = products;

      return products;
    } catch {
      return [];
    }
  };

  const deliveryResponse = (q: string) => {
    const lower = q.toLowerCase();

    if (lower.includes('zaria') || lower.includes('kaduna')) {
      return `🚚 Delivery information

• Zaria/Kaduna: estimated within 24 hours
• Shipping fee: FREE
• Location: ${LOCATION}

Please provide a complete delivery address at checkout.`;
    }

    return `🚚 Delivery information

• Zaria/Kaduna: estimated within 24 hours
• Outside Zaria/Kaduna: estimated 1–3 days
• Shipping fee: FREE for all orders
• Delivery estimates can vary because of address, logistics, holidays, weather, or other operational factors.

Our business is based at ${LOCATION}.`;
  };

  const responseFor = async (question: string): Promise<string> => {
    const q = question.toLowerCase().trim();

    /*
     * PASSWORD RESET / ACCOUNT RECOVERY
     */
    if (
      /(forgot.*password|password.*forgot|forgot.*login|reset.*password|password.*reset|account recovery|recover.*account|recover.*password|can't.*log in|cannot.*log in|unable.*log in|can't.*login|cannot.*login|unable.*login|lost.*password)/.test(
        q
      )
    ) {
      return `🔐 Password reset and account recovery

If you forgot your Gimbiya Value Store password:

1. Open the Login page.
2. Select "Forgot Password?"
3. Enter the email address associated with your account.
4. Check your email for a 6-digit verification code.
5. Enter the 6-digit code on the password-reset page.
6. Enter and confirm your new password.
7. Submit the form to reset your password.
8. Return to Login and sign in with your new password.

⏱️ The verification code expires in 10 minutes.

If you do not receive the code:
• Check your spam or junk folder.
• Make sure you entered the correct account email address.
• Request a new verification code if necessary.

For further assistance, contact ${SUPPORT_EMAIL}.

Never send your password, verification code, OTP, PIN, CVV, or card details through chat.`;
    }

    /*
     * DELIVERY
     */
    if (/(delivery|shipping|ship|deliver|courier)/.test(q)) {
      return deliveryResponse(q);
    }

    /*
     * RETURNS AND REFUNDS
     */
    if (
      /(return|refund|exchange|replace|damaged|defective|wrong item)/.test(q)
    ) {
      if (q.includes('how')) {
        return `🔄 How to request a return

1. Open My Orders.
2. Select the relevant order.
3. Use the available return option, if shown.
4. Follow the instructions provided by support.

Do not send an item back before receiving instructions.

The buyer generally bears return shipping costs based on prevailing market rates.

Contact ${SUPPORT_EMAIL} for help.`;
      }

      return `🔄 Returns and refunds

• Eligibility depends on the product, condition, reason, and applicable terms.
• Keep the original packaging, accessories, and proof of purchase.
• Buyer generally pays return shipping at prevailing market rates.
• Approved refunds are handled through the appropriate process for the original transaction.

Email ${SUPPORT_EMAIL} before sending an item back.`;
    }

    /*
     * WARRANTY
     */
    if (/(warranty|guarantee)/.test(q)) {
      return `🛡️ Manufacturer warranty

Warranty coverage applies only where provided by the manufacturer. The manufacturer's duration, exclusions, conditions, and claim process apply.

Keep your proof of purchase and contact ${SUPPORT_EMAIL} if you need help understanding a product's warranty.`;
    }

    /*
    * CONTACT / SUPPORT
    */
    if (/(contact|support|help|customer service|phone|telephone|call|number|address|location)/.test(q)) {
      return `📞 Gimbiya Value Store contact information

    📧 Email: ${SUPPORT_EMAIL}
    📞 Support: ${SUPPORT_PHONE}
    🚚 Logistics: ${LOGISTICS_PHONE}
    🏢 Head Office: ${HEAD_OFFICE_PHONE}

    📍 Location:
    ${LOCATION}

    🚚 Delivery:
    • Zaria/Kaduna: within 24 hours
    • Outside Zaria/Kaduna: 1–3 days
    • Shipping: FREE for all orders

    🛒 Minimum order:
    ₦${MINIMUM_ORDER_AMOUNT.toLocaleString()}

    💳 Part-payment:
    Minimum initial payment is ${MINIMUM_PART_PAYMENT_PERCENT}% of the order total.

    When contacting us about an order, include your order reference where relevant.

    Never send card numbers, PINs, CVVs, OTPs, verification codes, or passwords through chat.`;
    }

    /*
    * MINIMUM ORDER / PART-PAYMENT / DEPOSIT
    */
    if (
      /(minimum order|minimum.*amount|minimum.*purchase|least.*order|smallest.*order|15,000|15000|deposit|part.?payment|part payment|pay.*later|balance|installment|instalment)/.test(
        q
      )
    ) {
      if (
        /(deposit|part.?payment|part payment|pay.*later|balance|installment|instalment)/.test(
          q
        )
      ) {
        return `💳 Deposits and part-payments

    Gimbiya Value Store allows part-payment for eligible orders.

    • Minimum initial payment: ${MINIMUM_PART_PAYMENT_PERCENT}% of the order total
    • The remaining balance must be paid before the order can be completed according to the applicable order/payment terms.
    • Payment amounts and status are reflected in your order information.

    For questions about a specific payment or order balance, contact ${SUPPORT_EMAIL} or our Support team at ${SUPPORT_PHONE}.

    Never share your card number, PIN, CVV, OTP, verification code, or password through chat.`;
      }

      return `🛒 Minimum order amount

    The minimum order amount at Gimbiya Value Store is ₦${MINIMUM_ORDER_AMOUNT.toLocaleString()}.

    • Orders below ₦${MINIMUM_ORDER_AMOUNT.toLocaleString()} cannot proceed to checkout.
    • Shipping is FREE for all orders.

    Add products to your cart until the order subtotal reaches the minimum amount, then proceed to checkout.`;
    }

    /*
     * PAYMENTS
     */
    if (/(payment|paystack|card|transfer|ussd|bank)/.test(q)) {
      return `💳 Payments

Gimbiya Value Store uses Paystack for online payments.

1. Add products to your cart.
2. Proceed to checkout.
3. Select Paystack.
4. Complete payment on the secure Paystack page.

Available payment channels depend on what Paystack displays for your transaction.

Never share your card details, PIN, CVV, OTP, or password in chat.`;
    }

    /*
     * ORDERS / TRACKING
     */
    if (/(order|track|status)/.test(q)) {
      if (!isAuthenticated) {
        return '🔐 Please sign in to view your orders and tracking information. You can then open My Orders.';
      }

      const orders = await fetchOrders();

      if (!orders.length) {
        return '📦 I could not find any orders for your account. Open My Orders or contact support if you believe an order is missing.';
      }

      const latest = orders[0];

      return `📦 Latest order

Order: ${latest.orderReference || latest._id}
Status: ${(latest.status || 'pending').toUpperCase()}
Total: ₦${(latest.total || 0).toLocaleString()}
Date: ${
        latest.createdAt
          ? new Date(latest.createdAt).toLocaleDateString('en-NG')
          : 'Unavailable'
      }

Open My Orders for full details and available tracking options.`;
    }

    /*
     * REWARD POINTS
     */
    if (/(point|reward)/.test(q)) {
      if (!isAuthenticated) {
        return '⭐ Sign in to view your reward-point balance.';
      }

      return `[NAIRA_BAG] Reward points

Current balance: ${points} points
Approximate value: ₦${pointsToCurrency(points).toLocaleString()}

Your account's points rules and checkout display determine how points can be applied.`;
    }

    /*
     * CART
     */
    if (/(cart|basket)/.test(q)) {
      if (!cart.length) {
        return '🛒 Your cart is currently empty. Browse products and add an item to begin shopping.';
      }

      const lines = cart
        .slice(0, 4)
        .map(
          (item: CartItem) =>
            `• ${item.name} × ${item.quantity} — ₦${(
              item.price * item.quantity
            ).toLocaleString()}`
        )
        .join('\n');

      return `🛒 Your cart

${lines}${
        cart.length > 4 ? '\n• More items are in your cart' : ''
      }

Items: ${totalItems}
Subtotal: ₦${totalPrice.toLocaleString()}
Shipping: FREE`;
    }

    /*
     * PRODUCTS
     */
    if (/(product|item|featured|popular|recommend|find|shop)/.test(q)) {
      const products = await fetchProducts();

      const featured = products
        .filter((p) => p.isFeatured || p.isPopular)
        .slice(0, 5);

      if (!featured.length) {
        return '🛍️ Browse the Products page to explore available items. Tell me the product type you are looking for.';
      }

      return `⭐ Featured products

${featured
  .map(
    (p) => `• ${p.name} — ₦${p.price.toLocaleString()}`
  )
  .join('\n')}`;
    }

    /*
     * GREETINGS
     */
    if (
      /(hi|hello|hey|good morning|good afternoon|good evening)/.test(q)
    ) {
      return initialText(user?.name);
    }

    /*
     * THANK YOU
     */
    if (/(thank|thanks)/.test(q)) {
      return 'You are welcome! 😊 Let me know if you need help with anything else.';
    }

    /*
     * DEFAULT RESPONSE
     */
    return `I can help with delivery, orders, payments, deposits and part-payments, returns, warranties, products, accounts, password reset, minimum order requirements, and reward points.

    Try asking:
    • “How long is delivery?”
    • “What is the minimum order amount?”
    • “Do you accept part-payment?”
    • “How much deposit do I need?”
    • “How do I pay?”
    • “Track my order”
    • “I forgot my password”
    • “How do I reset my password?”
    • “How do I recover my account?”
    • “What is your warranty policy?”
    • “What are your contact numbers?”
    • “Contact support”`;
  };

  const sendMessage = async () => {
    const text = inputMessage.trim();

    if (!text || isTyping) return;

    setMessages((prev) => [
      ...prev,
      {
        id: `u-${Date.now()}`,
        text,
        sender: 'user',
        timestamp: new Date()
      }
    ]);

    setInputMessage('');
    setIsTyping(true);

    try {
      const answer = await responseFor(text);

      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          text: answer,
          sender: 'ai',
          timestamp: new Date()
        }
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          text: `Sorry, I could not complete that request. Please email ${SUPPORT_EMAIL}.`,
          sender: 'ai',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const formatMessage = (text: string) =>
    text.includes('[NAIRA_BAG]') ? (
      <NairaBagText text={text} size="md" />
    ) : (
      text
    );

  return (
    <main className="ai-chat-page">
      <header className="chat-header">
        <img
          src="/185_filtered_1.png"
          alt="Amaya"
          className="ai-avatar"
        />

        <div className='amaya_cust_care'>
          <strong>Amaya</strong>
          <span>Gimbiya shopping assistant</span>
        </div>
      </header>

      <div className="messages-wrapper" ref={wrapperRef}>
        <div className="chat-messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender}`}
            >
              <div className="message-bubble">
                <div className="message-text">
                  {formatMessage(message.text)}
                </div>

                <time>
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </time>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="message ai">
              <div className="message-bubble typing">•••</div>
            </div>
          )}
        </div>
      </div>

      <form
        className="chat-input"
        onSubmit={(e) => {
          e.preventDefault();
          void sendMessage();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Ask Amaya a question"
          maxLength={500}
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="sentences"
          spellCheck={true}
          inputMode="text"
          enterKeyHint="send"
          name="chat-message"
        />

        <button
          type="submit"
          disabled={!inputMessage.trim() || isTyping}
          aria-label="Send message"
        ></button>
      </form>
    </main>
  );
};

export default AIChat;